import { createApi } from "@reduxjs/toolkit/query/react";
import { pageLimit } from "src/modules/shared/components/table/data-table";

import { createBaseServiceWithInterceptor } from "../base-service";
import { ApiRequest, ApiResponse } from "../types";
import { Tag, TagPayload, TagResponse } from "./types";

const transformTagsResponse = (response: ApiResponse<TagResponse>) => ({
  ...response,
  items: response.items?.map((tag) => ({
    ...tag,
    label: tag.label
      ? {
          ...tag.label,
          isActive: !!tag.label,
        }
      : null,
    primaryButton: {
      ...tag.primaryButton,
      isActive: !!tag.primaryButton?.title,
    },
    secondaryButton: {
      ...tag.secondaryButton,
      isActive: !!tag.secondaryButton?.title,
    },
  })),
});

export const tagsApi = createApi({
  reducerPath: "tagsApi",
  baseQuery: createBaseServiceWithInterceptor(process.env.REACT_APP_API_URL),
  tagTypes: ["Tag"],
  endpoints: (builder) => ({
    getTags: builder.query<ApiResponse<Tag>, ApiRequest | undefined>({
      query: (params) => ({
        url: `tags`,
        params: {
          limit: pageLimit,
          page: params?.page || 1,
          sortDirection: params?.sort?.direction,
          sortField: params?.sort?.columnName,
          search: params?.search,
        },
      }),
      providesTags: ["Tag"],
      transformResponse: transformTagsResponse,
    }),
    editTag: builder.mutation<TagResponse, TagPayload>({
      query: (payload) => ({
        url: `tags/${payload.id}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Tag"],
    }),
    createTag: builder.mutation<TagResponse, Omit<TagPayload, "id">>({
      query: (payload) => ({
        url: "tags",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Tag"],
    }),
    deleteTag: builder.mutation<TagResponse, string>({
      query: (id) => ({
        url: `tags/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Tag"],
    }),
  }),
});

export const {
  useGetTagsQuery,
  useLazyGetTagsQuery,
  useEditTagMutation,
  useCreateTagMutation,
  useDeleteTagMutation,
  usePrefetch,
} = tagsApi;
